import { ApplicationWithId } from "../interfaces/IApplications";
import { AppUser } from "../interfaces/IUsers";
import { CategoryWithId } from "../interfaces/ICategories";
import { ContestWithId } from "../interfaces/IContests";
import { FirebaseApp } from "firebase/app";
import { Functions, getFunctions, httpsCallable } from "firebase/functions";
import { PresentationWithId } from "../interfaces/IPresentations";

// Typy dla PDF service
export interface PDFGenerationResponse {
  ok: boolean;
  pdfBase64: string; // base64
  filename: string;
  size?: number;
}

export interface PDFOptions {
  format?: "A4" | "A3" | "Letter";
  margin?: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  printBackground?: boolean;
  filename?: string;
}

export interface ApplicationConfirmationData {
  applicationId: string;
  contestName: string;
  userFirstName: string;
  userLastName: string;
  userOrganizationName?: string;
  userEmail: string;
  userPhoneNumber?: string;
  createTimestamp: Date;
  sendTimestamp: Date | null;
  contestStartDate: Date | null;
  contestEndDate: Date | null;
  applicationStatus: string;
  totalCost: number;
  totalParticipantsCount: number;
  totalPresentationsCount: number;
  categories: Array<{
    name: string;
    type: string;
    presentationsCount: number;
    costPerPresentation: number;
    totalCost: number;
    presentations: Array<{
      name: string;
      title?: string;
      participants: Array<{
        firstName: string;
        lastName: string;
      }>;
    }>;
  }>;
  invoiceData?: any;
  isInvoiceNeeded: boolean;
}

/**
 * PDFService - serwis do generowania dokumentów PDF
 *
 * Funkcjonalności:
 * - Generowanie potwierdzenia zgłoszenia
 * - Pobieranie PDF z Firebase Functions
 * - Automatyczne pobieranie plików
 * - Obsługa błędów
 */
class PDFService {
  private functions: Functions | null = null;
  private firebaseApp: FirebaseApp | null = null;

  constructor(app?: FirebaseApp) {
    this.firebaseApp = app || null;
  }

  private getFunctions(): Functions {
    if (!this.functions) {
      if (!this.firebaseApp) {
        throw new Error("Firebase app nie został przekazany do PDFService");
      }

      this.functions = getFunctions(this.firebaseApp);
    }
    return this.functions;
  }

  /**
   * Generowanie potwierdzenia zgłoszenia
   */
  async generateApplicationConfirmation(
    application: ApplicationWithId,
    contest: ContestWithId,
    categories: CategoryWithId[],
    user: AppUser,
    presentations?: PresentationWithId[],
  ): Promise<PDFGenerationResponse> {
    try {
      // Przygotuj dane kategorii z kosztami
      const categoriesData = this.prepareCategoriesData(application, categories, presentations);

      // Oblicz łączną liczbę uczestników i prezentacji
      const totalPresentationsCount = presentations?.length || 0;
      const totalParticipantsCount = Object.keys(
        application.participantPresentationsMap || {},
      ).length;

      // Przygotuj dane dla PDF
      const createTimestamp = this.convertToDate(application.createTimestamp);

      // Walidacja wymaganych pól przed wysłaniem
      const missingFields: string[] = [];
      if (!application.id) missingFields.push("application.id");
      if (!contest.name) missingFields.push("contest.name");
      if (!user.firstName) missingFields.push("user.firstName");
      if (!user.lastName) missingFields.push("user.lastName");
      if (!user.email) missingFields.push("user.email");
      if (!createTimestamp) missingFields.push("createTimestamp");
      if (application.status === undefined || application.status === null)
        missingFields.push("application.status");
      if (application.totalCost === undefined || application.totalCost === null)
        missingFields.push("application.totalCost");
      if (totalPresentationsCount === undefined || totalPresentationsCount === null)
        missingFields.push("totalPresentationsCount");
      if (totalParticipantsCount === undefined || totalParticipantsCount === null)
        missingFields.push("totalParticipantsCount");
      if (!Array.isArray(categoriesData)) missingFields.push("categories");
      // Jeśli są prezentacje, ale brak kategorii, to jest błąd
      if (
        Array.isArray(categoriesData) &&
        totalPresentationsCount > 0 &&
        categoriesData.length === 0
      ) {
        console.error("❌ [PDFService] Są prezentacje, ale brak kategorii w danych");
        missingFields.push("categories (pusta tablica mimo obecności prezentacji)");
      }
      if (application.isInvoiceNeeded === undefined || application.isInvoiceNeeded === null)
        missingFields.push("application.isInvoiceNeeded");

      if (missingFields.length > 0) {
        console.error("❌ [PDFService] Brak wymaganych danych przed wysłaniem:", missingFields);
        console.error("❌ [PDFService] Dane aplikacji:", {
          id: application.id,
          status: application.status,
          totalCost: application.totalCost,
          isInvoiceNeeded: application.isInvoiceNeeded,
        });
        console.error("❌ [PDFService] Dane użytkownika:", {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        });
        console.error("❌ [PDFService] Dane konkursu:", {
          name: contest.name,
        });
        console.error("❌ [PDFService] Dane kategorii:", {
          categoriesCount: categoriesData.length,
          categoriesData,
        });
        throw new Error(`Brak wymaganych danych zgłoszenia: ${missingFields.join(", ")}`);
      }

      // Przygotuj daty turnieju
      const contestStartDate = this.convertToDate(contest.startDate);
      const contestEndDate = this.convertToDate(contest.endDate);
      const sendTimestamp = application.sendTimestamp
        ? this.convertToDate(application.sendTimestamp)
        : null;

      const confirmationData: ApplicationConfirmationData = {
        applicationId: application.id || "",
        contestName: contest.name || "",
        userFirstName: user.firstName || "",
        userLastName: user.lastName || "",
        userOrganizationName: user.organizationName,
        userEmail: user.email || "",
        userPhoneNumber: user.phoneNumber,
        createTimestamp: createTimestamp,
        sendTimestamp: sendTimestamp,
        contestStartDate: contestStartDate,
        contestEndDate: contestEndDate,
        applicationStatus: application.status || "",
        totalCost: application.totalCost || 0,
        totalPresentationsCount: totalPresentationsCount || 0,
        totalParticipantsCount: totalParticipantsCount || 0,
        categories: categoriesData || [],
        invoiceData: application.invoiceData,
        isInvoiceNeeded: application.isInvoiceNeeded || false,
      };

      // Wywołaj Firebase Function
      const generateConfirmation = httpsCallable<
        { data: ApplicationConfirmationData; options?: PDFOptions },
        PDFGenerationResponse
      >(this.getFunctions(), "generateApplicationConfirmation");

      const result = await generateConfirmation({ data: confirmationData });

      if (!result.data.ok) {
        console.error("❌ [PDFService] Function zwróciła ok: false");
        throw new Error("Nie udało się wygenerować PDF");
      }

      if (!result.data.pdfBase64) {
        console.error("❌ [PDFService] Brak danych PDF w odpowiedzi");
        throw new Error("Brak danych PDF w odpowiedzi z serwera");
      }

      return result.data;
    } catch (error) {
      console.error("❌ [PDFService] Błąd podczas generowania potwierdzenia:", {
        error: error,
        message: error instanceof Error ? error.message : "Nieznany błąd",
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw new Error(
        `Nie udało się wygenerować potwierdzenia: ${
          error instanceof Error ? error.message : "Nieznany błąd"
        }`,
      );
    }
  }

  /**
   * Pobieranie i zapisanie PDF
   */
  async downloadPDF(pdfBase64: string, filename: string): Promise<void> {
    try {
      // Konwertuj base64 do blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Utwórz link do pobrania
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      // Automatyczne pobranie
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Zwolnij pamięć
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("❌ [PDFService] Błąd podczas pobierania PDF:", {
        error,
        message: error instanceof Error ? error.message : "Nieznany błąd",
        filename,
      });
      throw new Error("Nie udało się pobrać pliku PDF");
    }
  }

  /**
   * Generowanie i automatyczne pobranie potwierdzenia
   */
  async generateAndDownloadConfirmation(
    application: ApplicationWithId,
    contest: ContestWithId,
    categories: CategoryWithId[],
    user: AppUser,
    presentations?: PresentationWithId[],
  ): Promise<void> {
    try {
      // Generuj PDF
      const response = await this.generateApplicationConfirmation(
        application,
        contest,
        categories,
        user,
        presentations,
      );

      // Generuj nazwę pliku: {nazwa turnieju} - potwierdzenie zgłoszenia - {organization_name}.pdf
      const contestName = contest.name || "Turniej";
      const organizationName = user.organizationName || "Brak organizacji";
      // Usuń znaki specjalne z nazwy pliku
      const sanitizeFilename = (str: string) => {
        return str
          .replace(/[<>:"/\\|?*]/g, "_") // Zamień znaki specjalne na podkreślenia
          .replace(/\s+/g, "_") // Zamień spacje na podkreślenia
          .substring(0, 100); // Ogranicz długość
      };
      const filename = `${sanitizeFilename(contestName)} - potwierdzenie zgłoszenia - ${sanitizeFilename(organizationName)}.pdf`;

      // Pobierz automatycznie
      await this.downloadPDF(response.pdfBase64, filename);
    } catch (error) {
      console.error("❌ Błąd podczas generowania i pobierania PDF:", error);
      throw error;
    }
  }

  /**
   * Konwersja różnych formatów daty do Date
   */
  private convertToDate(timestamp: any): Date {
    if (!timestamp) {
      return new Date();
    }

    // Jeśli to już Date
    if (timestamp instanceof Date) {
      return timestamp;
    }

    // Jeśli to Firestore Timestamp
    if (timestamp && typeof timestamp.toDate === "function") {
      return timestamp.toDate();
    }

    // Jeśli to string lub number
    if (typeof timestamp === "string" || typeof timestamp === "number") {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Fallback
    console.warn(
      "⚠️ [PDFService] Nie można przekonwertować timestamp:",
      timestamp,
      "używam bieżącej daty",
    );
    return new Date();
  }

  /**
   * Przygotowanie danych kategorii z kosztami i prezentacjami
   */
  private prepareCategoriesData(
    application: ApplicationWithId,
    categories: CategoryWithId[],
    presentations?: PresentationWithId[],
  ): Array<{
    name: string;
    type: string;
    presentationsCount: number;
    costPerPresentation: number;
    totalCost: number;
    presentations: Array<{
      name: string;
      title?: string;
      participants: Array<{
        firstName: string;
        lastName: string;
      }>;
    }>;
  }> {
    const categoriesData: Array<{
      name: string;
      type: string;
      presentationsCount: number;
      costPerPresentation: number;
      totalCost: number;
      presentations: Array<{
        name: string;
        title?: string;
        participants: Array<{
          firstName: string;
          lastName: string;
        }>;
      }>;
    }> = [];

    // Jeśli mamy prezentacje, używamy ich do liczenia kategorii
    if (presentations && presentations.length > 0) {
      // Grupuj prezentacje według kategorii
      const presentationsByCategory = new Map<string, PresentationWithId[]>();
      presentations.forEach((presentation) => {
        const categoryId = presentation.categoryId;
        if (!presentationsByCategory.has(categoryId)) {
          presentationsByCategory.set(categoryId, []);
        }
        presentationsByCategory.get(categoryId)!.push(presentation);
      });

      categories.forEach((category, index) => {
        const categoryPresentations = presentationsByCategory.get(category.id || "") || [];
        const presentationsCount = categoryPresentations.length;

        if (presentationsCount > 0) {
          const costPerPresentation = category.presentationCost || category.participantCost || 0;
          const totalCost = presentationsCount * costPerPresentation;

          // Przygotuj dane prezentacji dla tej kategorii
          const presentationsData = categoryPresentations.map((presentation) => ({
            name: presentation.name,
            title: presentation.title,
            participants: presentation.participants.map((participant) => ({
              firstName: participant.firstName,
              lastName: participant.lastName,
            })),
          }));

          categoriesData.push({
            name: category.name,
            type: category.type,
            presentationsCount,
            costPerPresentation,
            totalCost,
            presentations: presentationsData,
          });
        }
      });
    } else {
      // Fallback - jeśli nie ma presentations, spróbuj totalCost
      if (application.totalCost > 0) {
        categoriesData.push({
          name: "Opłata za zgłoszenie",
          type: "OTHER",
          presentationsCount: 1,
          costPerPresentation: application.totalCost,
          totalCost: application.totalCost,
          presentations: [],
        });
      }
    }

    return categoriesData;
  }

  /**
   * Otwieranie PDF w nowej karcie (zamiast pobierania)
   */
  async openPDFInNewTab(pdfBase64: string): Promise<void> {
    try {
      // Konwertuj base64 do blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Utwórz URL i otwórz w nowej karcie
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      // Zwolnij pamięć po 1 sekundzie (czas na otwarcie)
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("❌ Błąd podczas otwierania PDF:", error);
      throw new Error("Nie udało się otworzyć pliku PDF");
    }
  }
}

// Eksport klasy - instancja będzie tworzona z app
export { PDFService };

// Tymczasowy singleton dla kompatybilności wstecznej
let pdfServiceInstance: PDFService | null = null;

export const getPDFService = (app: FirebaseApp): PDFService => {
  if (!pdfServiceInstance) {
    pdfServiceInstance = new PDFService(app);
  }
  return pdfServiceInstance;
};
